/**
 * Host guide beside the pages (#1165, copilot stages 3-4; escalation gap #1077).
 *
 * The host support chat (#1046) grown into a persistent side panel: a right-hand
 * column on wide screens (the page moves over, see index.css `host-guide-open`), a
 * bottom drawer on a phone. It is mounted once in App, so it stays open across
 * navigation inside the host area, and it knows which page is open (lib/hostGuide.ts).
 *
 * Three rules from the issue, kept structurally:
 * - preset buttons send a fixed message or open a page; they never call an action;
 * - an action proposal becomes a card that deep-links to the real UI's own confirm
 *   step. Nothing is sent, shared, closed or scheduled from here;
 * - when the guide is unsure, had no answer, or did not answer at all, the host gets
 *   one click to hand the conversation to the team, and a missing snapshot is said
 *   out loud ("answering with limited context"), never hidden.
 *
 * The conversation survives page loads within the tab (#1055): sessionStorage keyed
 * to the signed-in user. Arrival news (#1064) is unchanged: a quiet badge while the
 * panel is closed, one local message when the host opens it.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { useBottomOverlayInset } from '../../hooks/useBottomOverlayInset';
import { useTranslation } from '../../hooks/useTranslation';
import { HELPER_MARK_SRC } from '../../lib/brandMark';
import { actionTarget, presetsForPage, resolveHostPage, type ChatAction, type HostPage } from '../../lib/hostGuide';
import {
    escalateSupportChat,
    fetchSupportChatAvailability,
    fetchSupportChatContext,
    sendSupportChatMessage,
    type SupportEscalationReason,
} from '../../lib/supportApi';
import { splitChatReply } from '../../lib/supportChatLinks';
import { computeChatNews, toBaseline, type NewsBaseline, type NewsItem, type SessionFacts } from '../../lib/supportChatNews';
import { color, cx, typo } from '../../styles/chalkboardTokens';
import { btnPrimary, btnSecondary, btnTertiary, cardBase, kicker } from '../../styles/hostSurface';

type ChatMessage = {
    from: 'host' | 'support';
    text: string;
    /** true on a support reply that was handed to the team (email follow-up) */
    escalated?: boolean;
    /** Stage 4: proposals rendered as deep links; never executed here. */
    actions?: ChatAction[];
    /** The guide said it is unsure / had no answer: offer the handover. */
    lowConfidence?: boolean;
    /** The live-facts snapshot failed for this reply. */
    contextLimited?: boolean;
    /** The chat call itself failed: offer the handover. */
    failed?: boolean;
};

const CHAT_STORAGE_KEY = 'aot-support-chat-v1';
const MAX_STORED_MESSAGES = 50;

type StoredChat = {
    userId: string;
    open: boolean;
    sessionId?: string;
    messages: ChatMessage[];
};

const readStoredChat = (userId: string | undefined): StoredChat | null => {
    if (!userId || typeof window === 'undefined') {
        return null;
    }
    try {
        const raw = window.sessionStorage.getItem(CHAT_STORAGE_KEY);
        if (!raw) {
            return null;
        }
        const parsed = JSON.parse(raw) as StoredChat;
        if (parsed?.userId !== userId || !Array.isArray(parsed.messages)) {
            return null;
        }
        return parsed;
    } catch {
        return null;
    }
};

const writeStoredChat = (chat: StoredChat): void => {
    try {
        window.sessionStorage.setItem(
            CHAT_STORAGE_KEY,
            JSON.stringify({ ...chat, messages: chat.messages.slice(-MAX_STORED_MESSAGES) }),
        );
    } catch {
        // Storage full or blocked; the conversation still lives in memory
    }
};

const NEWS_BASELINE_KEY = 'aot-support-chat-news-v1';

const readNewsBaseline = (userId: string): NewsBaseline | null => {
    try {
        const raw = window.localStorage.getItem(NEWS_BASELINE_KEY);
        if (!raw) {
            return null;
        }
        const parsed = JSON.parse(raw) as NewsBaseline;
        if (parsed?.userId !== userId || typeof parsed.facts !== 'object' || parsed.facts === null) {
            return null;
        }
        return parsed;
    } catch {
        return null;
    }
};

const writeNewsBaseline = (baseline: NewsBaseline): void => {
    try {
        window.localStorage.setItem(NEWS_BASELINE_KEY, JSON.stringify(baseline));
    } catch {
        // No storage means no news next time; never an error the host sees
    }
};

const PAGE_LABEL_KEY: Record<HostPage['kind'], string> = {
    session: 'guide.pageSession',
    exercise: 'guide.pageExercise',
    programme: 'guide.pageProgramme',
    study: 'guide.pageStudy',
    home: 'guide.pageHome',
    other: 'guide.pageHome',
};

type EscalationState = { status: 'idle' } | { status: 'sending' } | { status: 'sent' } | { status: 'error' };

const HTTP_STATUS = (error: unknown): number | undefined =>
    typeof error === 'object' && error !== null && 'response' in error
        ? (error as { response?: { status?: number } }).response?.status
        : undefined;

export const HostGuidePanel = ({
    userId,
    visible,
    onSendAsFeedback,
}: {
    userId: string;
    /** False during the exercise moment (useHideSupportWidget): renders nothing. */
    visible: boolean;
    /** "Send as feedback instead": the widget swaps to the feedback form. */
    onSendAsFeedback: () => void;
}) => {
    const { t } = useTranslation();
    const location = useLocation();
    const bottomInset = useBottomOverlayInset();

    const [restored] = useState<StoredChat | null>(() => readStoredChat(userId));
    const [open, setOpen] = useState(restored?.open ?? false);
    const [messages, setMessages] = useState<ChatMessage[]>(restored?.messages ?? []);
    const [chatSessionId, setChatSessionId] = useState<string | undefined>(restored?.sessionId);
    const [input, setInput] = useState('');
    const [sending, setSending] = useState(false);
    const [available, setAvailable] = useState<boolean | null>(null);
    const [escalation, setEscalation] = useState<EscalationState>({ status: 'idle' });
    const listRef = useRef<HTMLDivElement>(null);

    const [news, setNews] = useState<NewsItem[]>([]);
    const latestFactsRef = useRef<SessionFacts[] | null>(null);
    const newsCheckedRef = useRef(false);

    const currentPage = useMemo(() => resolveHostPage(location.pathname), [location.pathname]);
    const presets = useMemo(() => presetsForPage(currentPage), [currentPage]);

    // Keep the stored conversation in step with what is on screen (#1055).
    useEffect(() => {
        writeStoredChat({ userId, open, sessionId: chatSessionId, messages });
    }, [userId, open, chatSessionId, messages]);

    // Beside, not on top: on wide screens the page moves over while the panel is open.
    useEffect(() => {
        const active = open && visible;
        document.body.classList.toggle('host-guide-open', active);
        return () => {
            document.body.classList.remove('host-guide-open');
        };
    }, [open, visible]);

    // Arrival news (#1064): fetch the whitelisted facts once per page load.
    useEffect(() => {
        if (!visible || newsCheckedRef.current) {
            return;
        }
        newsCheckedRef.current = true;
        void fetchSupportChatContext().then((sessions) => {
            if (!sessions) {
                return;
            }
            latestFactsRef.current = sessions;
            const baseline = readNewsBaseline(userId);
            if (!baseline) {
                writeNewsBaseline(toBaseline(userId, sessions));
                return;
            }
            const items = computeChatNews(baseline, sessions);
            if (items.length === 0) {
                writeNewsBaseline(toBaseline(userId, sessions));
            } else {
                setNews(items);
            }
        });
    }, [visible, userId]);

    // Deliver pending news as one local message when the host opens the panel.
    useEffect(() => {
        if (!open || news.length === 0) {
            return;
        }
        const lines = news.map((item) => {
            const parts: string[] = [];
            if (item.newResponses > 0) {
                parts.push(t('support.newsResponses', { count: item.newResponses }));
            }
            if (item.synthesisStale) {
                parts.push(t('support.newsSynthesisStale'));
            } else if (item.synthesisReady) {
                parts.push(t('support.newsSynthesisReady'));
            }
            return `- "${item.title}": ${parts.join(', ')} (/host/sessions/${item.id})`;
        });
        setMessages((current) => [...current, { from: 'support', text: `${t('support.newsIntro')}\n${lines.join('\n')}` }]);
        setNews([]);
        if (latestFactsRef.current) {
            writeNewsBaseline(toBaseline(userId, latestFactsRef.current));
        }
    }, [open, news, userId, t]);

    // Ask once per open whether the chat backend is configured.
    useEffect(() => {
        if (open && available === null) {
            void fetchSupportChatAvailability().then(setAvailable);
        }
    }, [open, available]);

    useEffect(() => {
        const list = listRef.current;
        if (list) {
            list.scrollTop = list.scrollHeight;
        }
    }, [messages, sending]);

    useEffect(() => {
        if (!open) {
            return;
        }
        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setOpen(false);
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open]);

    const send = useCallback(
        async (text: string) => {
            const trimmed = text.trim();
            if (!trimmed || sending) {
                return;
            }
            setSending(true);
            setEscalation({ status: 'idle' });
            setMessages((current) => [...current, { from: 'host', text: trimmed }]);
            try {
                const reply = await sendSupportChatMessage({ message: trimmed, sessionId: chatSessionId, currentPage });
                if (reply.sessionId) {
                    setChatSessionId(reply.sessionId);
                }
                setMessages((current) => [
                    ...current,
                    {
                        from: 'support',
                        text: reply.reply,
                        escalated: reply.escalated,
                        actions: reply.actions,
                        lowConfidence: reply.lowConfidence,
                        contextLimited: reply.contextLimited,
                    },
                ]);
            } catch (error) {
                if (HTTP_STATUS(error) === 503) {
                    // Backend says the chat service is not configured: degrade to feedback.
                    setAvailable(false);
                } else {
                    // The guide did not answer at all: say so, and offer the person (#1077 a).
                    setMessages((current) => [...current, { from: 'support', text: t('guide.failed'), failed: true }]);
                }
            } finally {
                setSending(false);
            }
        },
        [sending, chatSessionId, currentPage, t],
    );

    const submitInput = useCallback(() => {
        const text = input;
        setInput('');
        void send(text);
    }, [input, send]);

    const escalate = useCallback(
        async (reason: SupportEscalationReason) => {
            const transcript = messages.filter((m) => !m.failed && !m.escalated).map((m) => ({ from: m.from, text: m.text }));
            if (transcript.length === 0 || escalation.status === 'sending') {
                return;
            }
            setEscalation({ status: 'sending' });
            try {
                await escalateSupportChat({ messages: transcript, reason, sessionId: chatSessionId, currentPage });
                setEscalation({ status: 'sent' });
                setMessages((current) => [...current, { from: 'support', text: t('guide.escalated'), escalated: true }]);
            } catch {
                setEscalation({ status: 'error' });
            }
        },
        [messages, escalation.status, chatSessionId, currentPage, t],
    );

    if (!visible) {
        return null;
    }

    const lastIndex = messages.length - 1;

    return (
        <>
            {!open && (
                <button
                    type="button"
                    onClick={() => setOpen(true)}
                    style={{ bottom: bottomInset }}
                    className={cx(
                        'fixed right-4 z-40 flex items-center gap-2 rounded-md border py-1.5 pl-1.5 pr-3 text-sm shadow-lg backdrop-blur transition',
                        color.auto.borderSubtle,
                        color.auto.surface,
                        color.auto.textMuted,
                        'hover:shadow-emerald-500/10',
                    )}
                    aria-label={t('support.buttonHost')}
                >
                    <img src={HELPER_MARK_SRC} alt="" className="h-10 w-10 shrink-0 object-contain" />
                    {t('support.buttonHost')}
                    {news.length > 0 && (
                        <span
                            aria-label={t('support.newsBadge')}
                            className={cx(
                                'ml-2 inline-flex h-5 min-w-5 items-center justify-center rounded-md px-1 text-xs font-medium text-white',
                                color.auto.accentStrong,
                            )}
                        >
                            {news.length}
                        </span>
                    )}
                </button>
            )}

            {open && (
                <aside
                    role="dialog"
                    aria-label={t('guide.title')}
                    className={cx(
                        'fixed z-40 flex flex-col border shadow-xl',
                        // Phone: bottom drawer. Wide: full-height right column beside the page.
                        'inset-x-0 bottom-0 h-[85dvh] rounded-t-2xl border-t',
                        'lg:inset-y-0 lg:left-auto lg:right-0 lg:h-auto lg:w-[400px] lg:rounded-none lg:border-l lg:border-t-0',
                        color.auto.borderSubtle,
                        color.auto.bg,
                    )}
                >
                    <div className={cx('flex items-start justify-between gap-3 border-b px-4 py-3', color.auto.borderSubtle)}>
                        {/* AI Act art. 50(1): the AI disclosure is text in the header, always visible. */}
                        <div className="flex min-w-0 items-center gap-3">
                            <img src={HELPER_MARK_SRC} alt={t('guide.avatarAlt')} className="h-12 w-12 shrink-0 object-contain sm:h-14 sm:w-14" />
                            <div className="min-w-0">
                                <div className={cx('font-serif text-lg leading-tight', color.auto.textPrimary)}>{t('guide.title')}</div>
                                <div className={cx('text-xs', color.auto.textMuted)}>{t('guide.subtitle')}</div>
                                {currentPage && (
                                    <div className={cx('mt-0.5 truncate text-xs', color.auto.textFaint)}>{t(PAGE_LABEL_KEY[currentPage.kind])}</div>
                                )}
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={() => setOpen(false)}
                            aria-label={t('guide.close')}
                            className={cx('-mr-1 rounded-md px-2 py-0.5 text-xl leading-none', color.auto.textMuted)}
                        >
                            ×
                        </button>
                    </div>

                    {available === false ? (
                        <div className="flex flex-col gap-4 p-4">
                            <p className={cx('text-sm', color.auto.textMuted)}>{t('guide.intro')}</p>
                            <p className={typo.auto.bodyMuted}>{t('support.chatUnavailable')}</p>
                            <div className="flex justify-end gap-3">
                                <button type="button" onClick={() => setOpen(false)} className={btnTertiary}>
                                    {t('support.cancel')}
                                </button>
                                <button type="button" onClick={onSendAsFeedback} className={btnPrimary}>
                                    {t('support.chatFeedbackLink')}
                                </button>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div ref={listRef} className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-4 py-3">
                                {/* The introduction stays above the conversation, also when arrival news opens it. */}
                                <p className={cx('text-sm', color.auto.textMuted)}>{t('guide.intro')}</p>
                                {messages.map((message, index) => (
                                    <div key={`${index}-${message.from}`} className="flex flex-col gap-2">
                                        <div
                                            className={cx(
                                                'max-w-[90%] whitespace-pre-wrap break-words rounded-lg border px-3 py-2 text-sm',
                                                color.auto.borderSubtle,
                                                message.from === 'host'
                                                    ? cx('ml-auto', color.auto.surface, color.auto.textPrimary)
                                                    : cx('mr-auto', color.auto.textPrimary),
                                            )}
                                        >
                                            {message.from === 'support'
                                                ? splitChatReply(message.text).map((segment, segmentIndex) =>
                                                    segment.type === 'link' ? (
                                                        <Link key={segmentIndex} to={segment.path} className={cx('underline', color.auto.accentStrongText)}>
                                                            {segment.text}
                                                        </Link>
                                                    ) : (
                                                        <span key={segmentIndex}>{segment.text}</span>
                                                    ),
                                                )
                                                : message.text}
                                        </div>
                                        {message.contextLimited && (
                                            <p className={cx('text-xs', color.auto.textFaint)}>{t('guide.limitedContext')}</p>
                                        )}
                                        {message.escalated && (
                                            <p className={cx('text-xs', color.auto.success)}>{t('support.chatEscalated')}</p>
                                        )}
                                        {message.actions && message.actions.length > 0 && (
                                            <div className={cx(cardBase, 'flex flex-col gap-2 !p-3')}>
                                                <div className={kicker}>{t('guide.actionTitle')}</div>
                                                {message.actions.map((action) => {
                                                    const target = actionTarget(action);
                                                    return (
                                                        <Link key={`${action.kind}-${action.targetId}`} to={target.to} className={cx(btnSecondary, 'text-center')}>
                                                            {t(target.labelKey)}
                                                        </Link>
                                                    );
                                                })}
                                                <p className={cx('text-xs', color.auto.textFaint)}>{t('guide.actionNote')}</p>
                                            </div>
                                        )}
                                        {(message.lowConfidence || message.failed) && index === lastIndex && escalation.status !== 'sent' && (
                                            <div className={cx(cardBase, 'flex flex-col gap-2 !p-3')}>
                                                <p className={cx('text-sm', color.auto.textMuted)}>
                                                    {message.failed ? t('guide.failed') : t('guide.unsure')}
                                                </p>
                                                <button
                                                    type="button"
                                                    onClick={() => void escalate(message.failed ? 'chat_failed' : 'low_confidence')}
                                                    disabled={escalation.status === 'sending'}
                                                    className={btnPrimary}
                                                >
                                                    {escalation.status === 'sending' ? t('guide.escalating') : t('guide.escalateButton')}
                                                </button>
                                                {escalation.status === 'error' && (
                                                    <p className={cx('text-xs', color.auto.error)}>{t('guide.escalateError')}</p>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                ))}
                                {sending && <p className={cx('text-sm', color.auto.textFaint)}>{t('support.chatThinking')}</p>}
                            </div>

                            {presets.length > 0 && (
                                <div className={cx('border-t px-4 pb-2 pt-3', color.auto.borderSubtle)}>
                                    <div className={cx('mb-2 text-[11px] uppercase tracking-[0.18em]', color.auto.textFaint)}>{t('guide.presetsLabel')}</div>
                                    <div className="flex flex-wrap gap-2">
                                        {presets.map((preset) =>
                                            'to' in preset ? (
                                                <Link key={preset.key} to={preset.to} className={cx(btnTertiary, '!px-3 !py-1.5 !text-xs')}>
                                                    {t(preset.labelKey)}
                                                </Link>
                                            ) : (
                                                <button
                                                    key={preset.key}
                                                    type="button"
                                                    disabled={sending}
                                                    onClick={() => void send(t(preset.messageKey))}
                                                    className={cx(btnSecondary, '!px-3 !py-1.5 !text-xs')}
                                                >
                                                    {t(preset.labelKey)}
                                                </button>
                                            ),
                                        )}
                                    </div>
                                </div>
                            )}

                            <div className={cx('flex flex-col gap-2 border-t px-4 py-3', color.auto.borderSubtle)}>
                                <div className="flex items-end gap-2">
                                    <textarea
                                        value={input}
                                        onChange={(event) => setInput(event.target.value)}
                                        onKeyDown={(event) => {
                                            if (event.key === 'Enter' && !event.shiftKey) {
                                                event.preventDefault();
                                                submitInput();
                                            }
                                        }}
                                        rows={2}
                                        maxLength={4000}
                                        placeholder={t('guide.placeholder')}
                                        className={cx(
                                            'w-full resize-none rounded-md border p-2 text-base outline-none sm:text-sm',
                                            color.auto.borderSubtle,
                                            color.auto.surface,
                                            color.auto.textPrimary,
                                        )}
                                    />
                                    <button type="button" onClick={submitInput} disabled={sending || input.trim().length === 0} className={btnPrimary}>
                                        {t('support.send')}
                                    </button>
                                </div>
                                <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                                    <button type="button" onClick={onSendAsFeedback} className={cx('text-xs underline', color.auto.textMuted)}>
                                        {t('support.chatFeedbackLink')}
                                    </button>
                                    {messages.some((m) => m.from === 'host') && escalation.status !== 'sent' && (
                                        <button
                                            type="button"
                                            onClick={() => void escalate('host_request')}
                                            disabled={escalation.status === 'sending'}
                                            className={cx('text-xs underline', color.auto.textMuted)}
                                        >
                                            {t('guide.escalateAnyway')}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </aside>
            )}
        </>
    );
};

export default HostGuidePanel;
